import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FileDown, Landmark } from "lucide-react";
import { api, errMsg, download, today } from "@/lib/api";
import { useAuth, canWrite, isAdmin } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/Common";
import { PnFiltri, PnTabella, PnTabs, REGIME_LABEL } from "@/components/PrimaNotaParts";
import { Contabilita } from "@/components/coge/Contabilita";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";

const CAUSALI = ["Incasso cliente", "Pagamento fornitore", "Versamento in banca", "Prelievo", "Spese bancarie", "Stipendi", "F24 tasse e contributi", "Affitto", "Utenze", "Altro"];

const MovDialog = ({ mov, conti, onClose, onSaved }) => {
  const [m, setM] = useState(mov);
  const save = async () => {
    const body = { data: m.data, conto_id: m.conto_id, descrizione: m.descrizione, causale: m.causale, entrata: m.verso === "E" ? Number(m.importo) : 0, uscita: m.verso === "U" ? Number(m.importo) : 0 };
    try { m.id ? await api.put(`/primanota/movimenti/${m.id}`, body) : await api.post("/primanota/movimenti", body); toast.success("Movimento salvato"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const S = ({ v, k, opts, id }) => (
    <Select value={v} onValueChange={(x) => setM({ ...m, [k]: x })}><SelectTrigger data-testid={id}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([a, b]) => <SelectItem key={a} value={a}>{b}</SelectItem>)}</SelectContent></Select>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pn-mov-dialog">
        <DialogHeader><DialogTitle>{m.id ? "Modifica movimento" : "Nuovo movimento"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Data"><DateField testid="pn-mov-date" value={m.data} onChange={(v) => setM({ ...m, data: v })} /></Field>
          <Field label="Conto"><S v={m.conto_id} k="conto_id" id="pn-mov-account" opts={conti.map((c) => [c.id, c.nome])} /></Field>
          <Field label="Descrizione" className="sm:col-span-2"><Input data-testid="pn-mov-desc" value={m.descrizione} onChange={(e) => setM({ ...m, descrizione: e.target.value })} /></Field>
          <Field label="Causale"><S v={m.causale || "Altro"} k="causale" id="pn-mov-causale" opts={CAUSALI.map((c) => [c, c])} /></Field>
          <Field label="Entrata o uscita"><S v={m.verso} k="verso" id="pn-mov-verso" opts={[["E", "Entrata"], ["U", "Uscita"]]} /></Field>
          <Field label="Importo (\u20ac)"><Input type="number" step="0.01" min="0" data-testid="pn-mov-amount" value={m.importo} onChange={(e) => setM({ ...m, importo: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button className="bg-sky-600 hover:bg-sky-700" data-testid="pn-mov-save" onClick={save}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContiDialog = ({ conti, onClose, onSaved }) => {
  const [c, setC] = useState(conti.map((x) => ({ ...x })));
  const upd = (i, k, v) => setC(c.map((x, n) => (n === i ? { ...x, [k]: v } : x)));
  const save = async () => { try { await api.put("/primanota/conti", { conti: c.map((x) => ({ ...x, saldo_iniziale: Number(x.saldo_iniziale) || 0 })) }); toast.success("Conti salvati"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="pn-accounts-dialog">
        <DialogHeader><DialogTitle>Conti cassa e banca</DialogTitle></DialogHeader>
        {c.map((x, i) => (
          <div key={x.id || i} className="grid gap-2 sm:grid-cols-[1.2fr_0.8fr_1.4fr_0.8fr_auto]">
            <Input data-testid={`pn-acc-name-${i}`} placeholder="Nome" value={x.nome} onChange={(e) => upd(i, "nome", e.target.value)} />
            <Select value={x.tipo} onValueChange={(v) => upd(i, "tipo", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cassa">Cassa</SelectItem><SelectItem value="banca">Banca</SelectItem></SelectContent></Select>
            <Input placeholder="IBAN" value={x.iban || ""} onChange={(e) => upd(i, "iban", e.target.value)} />
            <Input type="number" step="0.01" placeholder="Saldo iniziale" data-testid={`pn-acc-start-${i}`} value={x.saldo_iniziale} onChange={(e) => upd(i, "saldo_iniziale", e.target.value)} />
            <Button variant="ghost" className="text-rose-600" disabled={c.length === 1} onClick={() => setC(c.filter((_, n) => n !== i))}>Togli</Button>
          </div>
        ))}
        <p className="text-xs text-slate-500">Il saldo iniziale \u00e8 il saldo del conto prima del primo movimento registrato. Incassi in contanti e corrispettivi in contanti vanno sul primo conto cassa, gli altri sul primo conto banca.</p>
        <DialogFooter>
          <Button variant="outline" data-testid="pn-acc-add" onClick={() => setC([...c, { nome: "", tipo: "banca", iban: "", saldo_iniziale: 0 }])}>Aggiungi conto</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" data-testid="pn-acc-save" onClick={save}>Salva conti</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function PrimaNota() {
  const { user } = useAuth();
  const [f, setF] = useState({ dal: `${today().slice(0, 4)}-01-01`, al: today(), conto_id: "" });
  const [rep, setRep] = useState(null);
  const [mov, setMov] = useState(null);
  const [conti, setConti] = useState(false);
  const [tab, setTab] = useState("cassa");
  const load = useCallback(() => api.get("/primanota", { params: f }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { if (tab === "cassa") load(); }, [load, tab]);
  if (!rep) return null;
  const write = canWrite(user) && rep.stato.attivo;
  const admin = isAdmin(user) && !user.demo;
  const del = async (r) => { if (!window.confirm("Eliminare questo movimento?")) return; try { await api.delete(`/primanota/movimenti/${r.id}`); toast.success("Movimento eliminato"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const aut = async (v) => { try { await api.put("/primanota/studio", { autorizzato: v }); toast.success(v ? "Studio autorizzato a consultare la prima nota" : "Autorizzazione allo studio revocata"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader title="Contabilità e prima nota" subtitle={rep.stato.attivo ? `${REGIME_LABEL[rep.regime] || ""} \u00b7 abbonamento attivo${rep.stato.scadenza ? ` fino al ${rep.stato.scadenza.split("-").reverse().join("/")}` : ""}` : "Abbonamento scaduto: consultazione in sola lettura"}>
        {tab === "cassa" && <>
        <Button variant="outline" className="bg-white" data-testid="pn-pdf" onClick={() => download("/primanota/pdf", f).catch((e) => toast.error(e.message))}><FileDown className="mr-2 h-4 w-4" />PDF</Button>
        {write && admin && <Button variant="outline" className="bg-white" data-testid="pn-accounts" onClick={() => setConti(true)}><Landmark className="mr-2 h-4 w-4" />Conti</Button>}
        {write && <Button className="bg-sky-600 hover:bg-sky-700" data-testid="pn-new" onClick={() => setMov({ data: today(), conto_id: rep.conti[0].id, descrizione: "", causale: "Altro", verso: "U", importo: "" })}><Plus className="mr-2 h-4 w-4" />Nuovo movimento</Button>}
        </>}
      </PageHeader>
      {admin && (
        <label className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm" data-testid="pn-studio-auth">
          <span><b>Autorizza il mio studio commercialista</b> a consultare e stampare la prima nota</span>
          <Switch data-testid="pn-studio-switch" checked={rep.studio_autorizzato} onCheckedChange={aut} />
        </label>
      )}
      <PnTabs tab={tab} setTab={setTab} regime={rep.regime} />
      {tab === "cassa" ? <>
      <PnFiltri f={f} set={setF} conti={rep.conti} />
      <PnTabella rep={rep} onEdit={write ? (r) => setMov({ ...r, verso: r.entrata ? "E" : "U", importo: r.entrata || r.uscita }) : null} onDelete={del} />
      </> : <Contabilita base="/coge" kind={tab} write={write} admin={admin} regime={rep.regime} />}
      {mov && <MovDialog mov={mov} conti={rep.conti} onClose={() => setMov(null)} onSaved={() => { setMov(null); load(); }} />}
      {conti && <ContiDialog conti={rep.conti} onClose={() => setConti(false)} onSaved={() => { setConti(false); load(); }} />}
    </div>
  );
}
